import SockJS from 'sockjs-client'
import { Client } from '@stomp/stompjs'
import orderApi from '@/utils/service/order'
import storeApi from '@/utils/service/storeApiRouter'
import rootApi from '@/utils/service/root'
import posApi from '@/utils/service/pos'
import { Buffer } from 'buffer'
import { isWebview } from '@/utils/storeId'
import {
	getSocketConnectionStatus,
	resolveSocketActionOptions,
	shouldNotifyDisconnectOnForeground,
	shouldShowDisconnectNotice,
	shouldReconnectOnForeground,
	shouldRefreshMenuAfterReconnect,
	shouldScheduleReconnectOnClose,
	STOMP_DEACTIVATE_TIMEOUT_MS,
} from '@/utils/socketReconnect'
import { shouldResyncOrdersAfterReconnect } from '@/utils/orderListResync'
import {
	shouldSkipDuplicatePush,
	deactivateWithTimeout,
} from '@/utils/socketPrintGuard'
import { pruneMessageMap } from '@/utils/messageMap'
import {
	getPrintJobKey,
	isAutomaticPrintSuppressed,
	PRINT_SUPPRESSION_TTL_MS,
	suppressAutomaticPrint as persistPrintSuppression,
} from '@/utils/printSuppression'
import { isIos } from '@/utils/versionHelper'
import {
	reportPrintLog,
	getReceiptMeta,
	getPrinterConnected,
	hasPrintableBillData,
	getBillLength,
} from '@/utils/printLog'

/**
 * 已送出停止補送 ACK 的 messageId 保留時間，只用來控制前端去重表大小。
 */
const DELIVERED_MESSAGE_TTL_MS = 10 * 60 * 1000

/**
 * 瀏覽器能不能直接用 Web Bluetooth 驅動出單機，關掉後出紙一律由 native 負責。
 * 瀏覽器不走 APP WebView 的 printDelivered ACK，
 * 而推播是整店廣播、後端跳不過任何一條連線——APP 的印表機沒連上時，
 * 那一列留在 PENDING、補送 5 次而瀏覽器每次都再吐一張，同一張單出 6 張紙。
 * 留成常數不刪整段：日後若改成按機型精準投遞，打開它就回到舊行為。
 */
const WEB_BLUETOOTH_PRINT_ENABLED = false

export default {
	namespaced: true,
	// modules: {}
	state: {
		socket: null, // 標記連線是否已初始化
		stompClient: null,
		heartbeatTimer: null, // 心跳計時器
		errorTimeout: null, // 斷線重連計時器
		errorCount: 0, // 斷線重連次數
		isConnected: false, // 實際 STOMP 是否已連上
		wantConnected: false, // 是否要維持連線；false 時關閉不自動重連
		notifyReconnect: false, // 此次連上是否提示「重新連線成功」
		missedPushWindow: false, // 這次連上之前中斷過，可能漏收推播，連上後要補資料
		disconnectedWhileInactive: false, // 背景期間斷線，回前景時補顯示一次提示
		waitPosReceiptQueue: [], //pos 待出單資料
		waitPosSecondReceiptQueue: [], //pos 2 待出單資料
		waitKioskReceiptQueue: [], // kiosk 待出單資料
		isLockPosPrinter: false, // pos 出單鎖
		isLockPosSecondPrinter: false, // pos 2 出單鎖
		isLockKioskPrinter: false, // kiosk 出單鎖
		isConnecting: false, // 是否正在建立連線，避免併發
		socketEpoch: 0, // 用來忽略舊連線的延遲 close
		subscriptions: {}, // 記錄所有訂閱對象，key 為訂閱名稱（如 'announcementPlatform'）
		processedMessages: new Map(), // 消息去重機制：存儲已處理的 messageId 和時間戳（非響應式，僅用於去重判斷）
		deliveredMessages: new Map(), // 列印可靠性：本機已送出 ACK 的 messageId 和時間戳（非響應式）
		recentPushes: new Map(), // 列印可靠性：剛收到的列印推播 messageId 和時間戳，擋多條連線同時投遞（非響應式）
		nativePrintsInFlight: new Map(), // 已轉交 Native、尚未收到成功或略過結果的 messageId（非響應式）
		needResyncOrderList: false, // WebSocket 重連後通知訂單管理補同步
	},
	mutations: {
		setNeedResyncOrderList(state, value) {
			state.needResyncOrderList = value
		},
	},
	getters: {
		connectionStatus(state) {
			return getSocketConnectionStatus({
				isConnected: state.isConnected,
				wantConnected: state.wantConnected,
			})
		},
	},
	actions: {
		initWebSocket(ctx) {
			const { dispatch, state } = ctx
			state.wantConnected = true
			// 先清理舊的心跳定時器（如果有）
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}
			dispatch('connection')
		},
		startHeartbeat(ctx) {
			const { dispatch, state } = ctx
			// 清理可能存在的舊定時器
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}
			// 連線成功後才啟動心跳定時器，每 30 秒發送一次
			state.heartbeatTimer = setInterval(() => {
				try {
					state.stompClient.publish({
						destination: '/app/connect/backstage',
						headers: {},
						body: 'PING',
					})
				} catch (err) {
					reportPrintLog('WebSocket心跳發送失敗', {
						error: err?.message ?? String(err),
					})
					dispatch('reconnection', '心跳發送失敗')
				}
			}, 30000)
		},
		async connection(ctx) {
			const { state, dispatch, commit, rootState } = ctx
			if (!state.wantConnected) {
				return
			}
			if (state.isConnecting) {
				$devLog(
					'%cWebSocket 連線進行中，略過新的連線請求',
					'color:orange;background-color:#fff;',
				)
				return
			}
			if (state.stompClient && state.stompClient.connected) {
				$devLog(
					'%cWebSocket 已連線，略過新的連線請求',
					'color:blue;background-color:#fff;',
				)
				return
			}

			// 佔位必須在任何 await 之前。取 wsToken 是非同步的，若等到建完 Client 才設，
			// 這段空窗內第二次 connection() 會整個穿過上面的守門，建出兩個 Client 並各自
			// activate；state.stompClient 只留得住後者，前者變成孤兒連線，
			// 而兩者的 onConnect 都會 subscribe，同一則列印推播就被處理兩次。
			state.isConnecting = true
			const connectionEpoch = state.socketEpoch

			if (state.socket === null && state.stompClient === null) {
				let wsToken = rootState.storage.wsToken
				let shouldPersistWsToken = false
				const storeId = rootState.storage.storeId
				const authToken = rootState.storage.authToken

				const before = {
					client: state.stompClient,
					connected: state.isConnected,
				}

				if (!wsToken && authToken) {
					try {
						const res = await rootApi.getWsToken()
						wsToken = res.data
						shouldPersistWsToken = true
					} catch (err) {
						$devLog(
							'%c取得 wsToken 失敗',
							'color:red;background-color:#fff;',
							err,
						)
					}
				}

				// 上面的守門是在 await 之前判的，而 await 期間任何一條 disconnect 都會把
				// isConnecting 清回 false、把 stompClient 設成 null，讓第二次 connection()
				// 穿過全部守門。醒來後不重驗的話，兩邊各自 new Client 並 activate，
				// state 只留得住後寫入的那個，先寫入的變成沒人關得掉的孤兒連線。
				if (
					connectionEpoch !== state.socketEpoch ||
					state.stompClient !== before.client
				) {
					// 已經有人建好了，這條讓給它。不清 isConnecting——那面旗現在歸它所有，
					// 由它的 onConnect / onStompError / onWebSocketClose 負責清。
					$devLog(
						'%c取 wsToken 期間已有連線建立，放棄本次',
						'color:orange;background-color:#fff;',
					)
					return
				}
				if (!state.wantConnected || state.isConnected !== before.connected) {
					// 這段空窗內被要求斷線或已切換連線世代，就不要再把舊連線建起來
					state.isConnecting = false
					return
				}
				if (shouldPersistWsToken) {
					commit('storage/setWsToken', wsToken, { root: true })
				}

				const params = new URLSearchParams()
				if (wsToken) {
					params.append('wsToken', wsToken)
				}
				if (storeId) {
					params.append('storeId', storeId)
				}

				// 版號守門（列印可靠性）：只送「當前平台」的 Native 版號（去掉開頭 v）。
				// 漏掉 iPhone/iPad/iPod 會讓 iOS 拿去比 Android 門檻，整店靜默退回舊列印且無日誌。
				const isApple = isIos()
				const rawVersion = isApple
					? rootState.storage.iosVersion
					: rootState.storage.androidVersion
				if (rawVersion) {
					params.append(
						isApple ? 'iosVersion' : 'androidVersion',
						String(rawVersion).replace(/^[vV]/, ''),
					)
				}

				// 執行環境標記：後端據此分辨「純瀏覽器」與「沒帶版號的舊版 APP」。
				// 而這一行決定的是整店走不走 print_job 新流程。
				params.append('clientRuntime', isWebview() ? 'APP' : 'WEB')

				const wsUrl = `${
					process.env.VUE_APP_BASE_API
				}/app/websocket?${params.toString()}`

				const epoch = connectionEpoch
				// 用區域常數持有這一條連線，讓關閉回呼讀得到「屬於自己這條連線」的斷線原因。
				const client = new Client({
					webSocketFactory: () =>
						new SockJS(wsUrl, null, { transports: ['websocket'] }),
					reconnectDelay: 0, // 停用內建自動重連，統一由 onErrorReConnection 管理
					debug: () => {},
					onConnect: () => {
						if (epoch !== state.socketEpoch) {
							return
						}
						state.isConnecting = false
						dispatch('successCallback')
					},
					onStompError: err => {
						if (epoch !== state.socketEpoch) {
							return
						}
						$devLog('%cSTOMP 錯誤', 'color:red;background-color:#fff;', err)
						reportPrintLog('WebSocket STOMP錯誤', {
							error: err?.message ?? String(err),
						})
						state.isConnecting = false
						dispatch('onErrorReConnection', 'STOMP 協定錯誤')
					},
					onWebSocketClose: evt => {
						const disconnectReason = client.__disconnectReason || null
						const wasClean = evt?.wasClean ?? null
						const closeMeta = {
							// 1006 代表連 close frame 都沒收到（網路斷、裝置休眠、Wi-Fi 切換），
							// 1001 是對端優雅離開（後端重啟／部署），兩者是完全不同的故障
							code: evt?.code ?? null,
							wasClean,
							// 前端自己關的時候這裡是 SockJS 硬塞的 'Normal closure'，不是伺服器給的
							closeReason: evt?.reason || null,
							disconnectReason,
							initiator: !disconnectReason
								? '非前端主動'
								: wasClean
								? '前端主動'
								: '前端主動但連線先斷',
							isConnected: state.isConnected,
							isConnecting: state.isConnecting,
							isStaleConnection: epoch !== state.socketEpoch,
						}
						reportPrintLog('WebSocket連線關閉', closeMeta)
						if (closeMeta.isStaleConnection) {
							return
						}
						const wasConnected = state.isConnected
						const isAppActive = !!rootState.storage.active
						if (wasConnected && isAppActive) {
							commit(
								'showMessage',
								{
									text: state.wantConnected
										? '連線已中斷，正在重新連線'
										: '連線已中斷',
									color: 'error',
									duration: 2500,
								},
								{ root: true },
							)
						}
						if (wasConnected) {
							state.disconnectedWhileInactive = !isAppActive
						}
						state.isConnected = false
						state.isConnecting = false
						if (state.wantConnected) {
							dispatch(
								'onErrorReConnection',
								`連線關閉 code=${evt?.code ?? '未知'}`,
							)
						}
					},
				})
				state.stompClient = client
				state.socket = true
			}

			// 啟動連線
			if (state.stompClient && !state.stompClient.active) {
				try {
					state.stompClient.activate()
				} catch (err) {
					$devLog(
						'%cWebSocket 啟動失敗',
						'color:red;background-color:#fff;',
						err,
					)
					reportPrintLog('WebSocket啟動失敗', {
						error: err?.message ?? String(err),
					})
					state.isConnecting = false
					state.socket = null
					state.stompClient = null
				}
			}
		},
		successCallback(ctx) {
			const { state, dispatch, commit } = ctx
			state.isConnected = true
			state.disconnectedWhileInactive = false
			if (state.notifyReconnect) {
				this.commit('showMessage', {
					text: '重新連線成功!',
					duration: 2500,
				})
				state.notifyReconnect = false
			}
			// 判斷依據是「這次連上之前中斷過」而非重試次數：背景斷線不排退避重試、
			// zombie 連線由前景恢復直接重建，兩者都不會累加 errorCount。
			const { missedPushWindow } = state
			state.missedPushWindow = false
			// 斷線期間可能漏收 MENU 推播；只在仍位於 POS 頁時補標過期。
			if (
				shouldRefreshMenuAfterReconnect(
					missedPushWindow,
					window.location.pathname,
				)
			) {
				commit('setRefreshMenu', true, { root: true })
			}
			// 訂單推播沒有補送機制，漏掉的訂單得由訂單管理頁自己補查。
			if (
				shouldResyncOrdersAfterReconnect(
					missedPushWindow,
					window.location.pathname,
				)
			) {
				commit('setNeedResyncOrderList', true)
			}
			ctx.commit(
				'setNetworkDisconnect',
				{ modal: false, manuallyLoading: false },
				{ root: true },
			)
			state.errorCount = 0
			if (state.errorTimeout) {
				clearTimeout(state.errorTimeout)
				state.errorTimeout = null
			}
			$devLog('%c socket connect', 'color:green;background-color:#fff;')

			// 連接成功後才啟動心跳定時器
			dispatch('startHeartbeat')

			// 立即發送一次心跳
			state.stompClient.publish({
				destination: '/app/connect/backstage',
				headers: {},
				body: 'PING',
			})

			try {
				// 訂閱平台公告通道
				dispatch('resubscribe', {
					key: 'announcementPlatform',
					destination: '/announcement/platform',
					callback: async msg => {
						const data = JSON.parse(msg.body)
						$devLog('/announcement/platform', data)
						if (data.event === 'START_MAINTENANCE') {
							ctx.commit(
								'setMaintenance',
								{
									modal: true,
									title: data.title,
									message: data.message,
								},
								{ root: true },
							)
							return
						}

						if (data.event === 'END_MAINTENANCE') {
							ctx.commit(
								'setMaintenance',
								{
									modal: false,
									title: '',
									message: '',
								},
								{ root: true },
							)
						}
					},
				})

				// 訂閱後台通道
				dispatch('resubscribe', {
					key: 'userTopicBackstage',
					destination: '/user/topic/backstage',
					callback: msg => {
						const data = JSON.parse(msg.body)
						if (!data.storeId) {
							const shouldReconnect = data.m === '1' || data.m === 1
							if (shouldReconnect) {
								$devLog(
									'%c接收到異常通知，重新建立連線',
									'color:red;background-color:#fff;',
									data,
								)
								dispatch('reconnection', '收到後端異常通知')
							}
							return
						}
						$devLog('/topic/backstage', data)
						const { rootState } = ctx
						if (rootState.storage.storeId === data.storeId) {
							if (data.action === 'resetPassword') {
								const logoutId = data.accounts.indexOf(rootState.storage.userId)
								if (logoutId !== -1) {
									this.commit('showMessage', '密碼已修改，三秒後即將登出！')
									dispatch('disconnect', '密碼被修改，強制登出')
									setTimeout(() => this.dispatch('logout'), 3000)
								}
							} else {
								if (data.orderSource !== 'BACK' && data.insertNotice) {
									commit('onPlayNoticeAudio', true, { root: true })
								} else if (data.bookingNotice) {
									commit('onPlayBookingOrderNoticeAudio', true, { root: true })
								} else if (data.idleNotice) {
									commit('onPlayIdleOrderNoticeAudio', true, { root: true })
								}
								// 更改訂單需要彈窗提示
								if (data.thirdPartyDeliveryOrderHasChanged) {
									// uberEats訂單修改、取消通知
									commit('order/setThirdPartyDeliveryOrderHasChanged', true, {
										root: true,
									})
									// 將所有ws收到的變動訂單加入佇列(主要作為訂單變動視窗連續觸發用)
									commit('order/enqueueModifyOrder', data, { root: true })
								}

								const pathname = window.location.pathname
								if (
									pathname !== null &&
									!pathname.startsWith('/order-manage')
								) {
									rootState.orderManagerRedTag = true
								}
								rootState.order.onWSMessageStoreMeal = {
									...data,
									_new: true,
								}
							}
						}
					},
				})

				// 訂閱列印通道
				// skipDedupe：補送沿用同一 messageId，套用 processedMessages 的通用去重
				// 會把全部補送擋掉、補送機制靜默失效。改用 deliveredMessages 判定，見下方 callback。
				dispatch('resubscribe', {
					key: 'userTopicPrinter',
					destination: '/user/topic/printer',
					skipDedupe: true,
					callback: async msg => {
						// {String} machineType, {byte[]} clientReceipt, {byte[]} storeReceipt;
						let data
						try {
							data = JSON.parse(msg.body)
						} catch (error) {
							reportPrintLog('列印推送解析失敗', {
								error: error?.message ?? String(error),
							})
							return
						}
						const { rootState } = ctx
						if (rootState.storage.storeId === Number(data.storeId)) {
							$devLog('/topic/printer', data)
							reportPrintLog('收到列印推送', {
								machineType: data.machineType,
								messageId: data.messageId,
								receipts: getReceiptMeta(data),
							})
							// 同一則推播從多條連線同時進來時只放行第一則。
							// 這道閘門必須同步、且排在所有 await 之前，理由見 socketPrintGuard。
							if (shouldSkipDuplicatePush(state.recentPushes, data.messageId)) {
								reportPrintLog('列印推送重複投遞略過', {
									machineType: data.machineType,
									messageId: data.messageId,
								})
								return
							}
							// 已送出停止補送 ACK 的工作不再重複處理
							if (
								data.messageId &&
								state.deliveredMessages.has(data.messageId)
							) {
								reportPrintLog('列印重送已交付略過', {
									machineType: data.machineType,
									messageId: data.messageId,
								})
								return
							}
							// 這台 APP 曾在目標藍牙不可用時看過此工作。後續補送只在本機略過，
							// 不替其他可能已連線的 APP 回 ACK。
							if (isAutomaticPrintSuppressed(data)) {
								reportPrintLog('列印重送本機略過', {
									machineType: data.machineType,
									messageId: data.messageId,
									reason: '三分鐘內曾確認目標藍牙不可用',
								})
								return
							}
							const printJobKey = getPrintJobKey(data)
							pruneMessageMap(
								state.nativePrintsInFlight,
								PRINT_SUPPRESSION_TTL_MS,
							)
							if (printJobKey && state.nativePrintsInFlight.has(printJobKey)) {
								reportPrintLog('列印Native處理中略過', {
									machineType: data.machineType,
									messageId: data.messageId,
								})
								return
							}
							if (printJobKey) {
								state.nativePrintsInFlight.set(printJobKey, Date.now())
							}
							dispatch('printWithDataAndPlatform', data)
								.then(forwarded => {
									if (!forwarded && printJobKey) {
										state.nativePrintsInFlight.delete(printJobKey)
									}
								})
								.catch(() => {
									if (printJobKey) {
										state.nativePrintsInFlight.delete(printJobKey)
									}
								})
						} else {
							reportPrintLog('列印推送店家不符', {
								machineType: data.machineType,
								messageId: data.messageId,
								pushStoreId: data.storeId,
								currentStoreId: rootState.storage.storeId,
							})
						}
					},
				})

				// 訂閱公告通道
				if (ctx.rootState.storage.manager) {
					// 高權限者 不用訂閱公告
				} else {
					dispatch('resubscribe', {
						key: 'userAnnouncement',
						destination: '/user/announcement',
						callback: async msg => {
							const data = JSON.parse(msg.body)
							$devLog('/announcement', data)

							if (data.id) {
								this.commit('storage/removeHiddenAnnouncement', data.id)
								this.commit('storage/removeShownAnnouncement', data.id)
							}
							this.commit(
								'storage/setAnnouncementDisplayShow',
								data.notify === 'REFRESH'
									? {
											status: `REFRESH${Math.random() * 100000}${
												Math.random() * 100000
											}`,
											id: null,
									  }
									: {
											status: `REFRESH${Math.random() * 100000}${
												Math.random() * 100000
											}`,
											id: data.id,
									  },
							)
						},
					})
				}
				const storeId = ctx.rootState.storage.storeId
				if (storeId) {
					// 訂閱刷新通道
					dispatch('resubscribe', {
						key: 'refresh',
						destination: `/refresh/${storeId}`,
						callback: async msg => {
							const data = JSON.parse(msg.body)
							$devLog('/refresh', data)
							if (storeId !== data.storeId) {
								$devLog('店家id不合')
								return
							}
							if (data.type === 'SETTING') {
								// 更新店家設定
								ctx.commit('setRefreshSetting', true, { root: true })
							}

							if (data.type === 'MENU') {
								// 當前在pos頁才刷新
								const pathname = window.location.pathname
								if (pathname !== null && pathname.startsWith('/store-pos')) {
									ctx.commit('setRefreshMenu', true, { root: true })
								}
							}
						},
					})
				}
			} catch (error) {
				$devLog(
					'%c訂閱失敗，連接可能未完全建立:',
					'color:red;background-color:#fff;',
					error,
				)
			}
		},
		/**
		 * @param {object} ctx
		 * @param {string} [reason] 觸發這輪重連的原因（STOMP 錯誤／連線關閉 code…），
		 *   會沿著退避 timer 一路帶到最終的 disconnect 埋點
		 */
		onErrorReConnection(ctx, reason) {
			const { dispatch, state, rootState } = ctx
			const triggerReason = reason || '未標記來源'
			if (
				!shouldScheduleReconnectOnClose({
					wantConnected: state.wantConnected,
					isAppActive: !!rootState.storage.active,
				})
			) {
				return
			}
			if (state.errorTimeout) {
				return
			}
			reportPrintLog('WebSocket觸發重連', {
				triggerReason,
				errorCount: state.errorCount,
				manuallyLoading: !!ctx.rootState.networkDisconnect.manuallyLoading,
				maintenanceModal: !!rootState.maintenance.modal,
			})
			if (rootState.maintenance.modal) {
				state.errorCount = 1
			}
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}

			state.isConnected = false
			if (state.errorCount < 30) {
				const now = new Date()
				const formattedTime = new Intl.DateTimeFormat('zh-TW', {
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false,
				}).format(now)

				if (ctx.rootState.networkDisconnect.manuallyLoading) {
					$devLog(
						`%c手動重試(不計自動重試總數) - 當前時間: ${formattedTime}`,
						'color:red;background-color:#fff;',
					)
				} else {
					state.errorCount++
					$devLog(
						`%c斷線重連嘗試次數 ${state.errorCount} - 當前時間: ${formattedTime}`,
						'color:red;background-color:#fff;',
					)
				}

				if (state.errorCount >= 3) {
					ctx.commit(
						'setNetworkDisconnect',
						{ modal: true, manuallyLoading: false },
						{ root: true },
					)
				}
				// 指數退避 + 隨機抖動（Equal Jitter，區間 [cap/2, cap]），cap 8s：起步快、削平尖峰同步重連雪崩
				const backoffCeil = Math.min(
					8000,
					1000 * 2 ** Math.max(0, state.errorCount - 1),
				)
				const reconnectDelay = Math.round(
					backoffCeil / 2 + Math.random() * (backoffCeil / 2),
				)
				state.errorTimeout = setTimeout(() => {
					state.errorTimeout = null
					if (
						!shouldScheduleReconnectOnClose({
							wantConnected: state.wantConnected,
							isAppActive: !!rootState.storage.active,
						})
					) {
						return
					}
					dispatch('reconnection', `退避重試（${triggerReason}）`)
				}, reconnectDelay)
				return
			}

			$devLog(
				`%c超過斷線自動連線上限,導向登出`,
				'color:red;background-color:#fff;',
			)
			reportPrintLog('WebSocket重連超過上限', {
				triggerReason,
				errorCount: state.errorCount,
			})
			dispatch('disconnect', '重連超過 30 次上限，導向登出')
			state.errorCount = 0
			this.dispatch('logout')
		},
		cancelPendingReconnect({ state }) {
			if (state.errorTimeout) {
				clearTimeout(state.errorTimeout)
				state.errorTimeout = null
			}
		},
		/**
		 * @param {object} ctx
		 * @param {string|object} [payload] 觸發重連的原因與提示設定
		 */
		async reconnection(ctx, payload) {
			const { dispatch, state } = ctx
			const { reason: reconnectReason, suppressDisconnectNotice } =
				resolveSocketActionOptions(payload)
			reportPrintLog('WebSocket開始重連流程', { reconnectReason })
			await dispatch('disconnect', {
				reason: `重連流程：${reconnectReason}`,
				suppressDisconnectNotice,
			})
			state.notifyReconnect = true
			state.missedPushWindow = true
			storeApi.platformParam.get('BACKSTAGE_H5_VERSION').catch(_ => {
				// 主要讓401時能導登入頁，不用一直重試
			})
			dispatch('initWebSocket')
		},
		async ensureConnected(ctx) {
			const { dispatch, state, commit } = ctx
			const stompConnected = !!state.stompClient?.connected
			if (
				!shouldReconnectOnForeground({
					isConnected: state.isConnected,
					stompConnected,
				})
			) {
				return
			}
			if (state.errorTimeout) {
				clearTimeout(state.errorTimeout)
				state.errorTimeout = null
			}
			reportPrintLog('WebSocket前景恢復立刻重連', {
				isConnected: state.isConnected,
				stompConnected,
				isConnecting: state.isConnecting,
			})
			if (
				shouldNotifyDisconnectOnForeground({
					isConnected: state.isConnected,
					stompConnected,
					disconnectedWhileInactive: state.disconnectedWhileInactive,
				})
			) {
				commit(
					'showMessage',
					{
						text: '連線已中斷，正在重新連線',
						color: 'error',
						duration: 2500,
					},
					{ root: true },
				)
			}
			state.disconnectedWhileInactive = false
			await dispatch('reconnection', {
				reason: 'APP 回到前景',
				suppressDisconnectNotice: true,
			})
		},
		/**
		 * @param {object} ctx
		 * @param {string|object} [payload] 這次主動斷線的原因與提示設定
		 */
		async disconnect(ctx, payload) {
			const { state, commit } = ctx
			const { reason: disconnectReason, suppressDisconnectNotice } =
				resolveSocketActionOptions(payload)
			// 標記要在 deactivate 之前寫上去，關閉事件才讀得到。
			if (state.stompClient && !state.stompClient.__disconnectReason) {
				state.stompClient.__disconnectReason = disconnectReason
			}
			reportPrintLog('WebSocket執行disconnect', {
				disconnectReason,
				hasClient: state.stompClient !== null,
				isConnected: state.isConnected,
			})
			if (
				shouldShowDisconnectNotice({
					isConnected: state.isConnected,
					suppressDisconnectNotice,
				})
			) {
				commit(
					'showMessage',
					{
						text: '連線已中斷',
						color: 'error',
						duration: 2500,
					},
					{ root: true },
				)
			}
			state.wantConnected = false
			state.isConnected = false
			state.isConnecting = false
			state.notifyReconnect = false
			state.missedPushWindow = false
			state.socketEpoch += 1

			state.subscriptions = {}
			state.processedMessages.clear()
			// 清理定時器
			if (state.heartbeatTimer) {
				clearInterval(state.heartbeatTimer)
				state.heartbeatTimer = null
			}
			if (state.errorTimeout) {
				clearTimeout(state.errorTimeout)
				state.errorTimeout = null
			}
			if (state.stompClient !== null) {
				const client = state.stompClient
				state.socket = null
				state.stompClient = null
				// 不 await：背景已死的 deactivate 不可擋住前景立刻重連；逾時後會強制斷線。
				// epoch 已加，舊連線的 onConnect / onClose 會被忽略。
				void deactivateWithTimeout(client, STOMP_DEACTIVATE_TIMEOUT_MS)
				$devLog('%c socket disconnect', 'color:red;background-color:#fff;')
			}
		},
		async updateOrderStatus(ctx, data) {
			const { id, status, thirdPartyDelivery } = data
			const { rootState } = ctx

			await orderApi.updateOrderStatus({
				orderId: id,
				storeId: rootState.storage.storeId,
				status,
				deliveryOrder: thirdPartyDelivery,
			})
		},
		async printWithDataAndPlatform({ state, rootState, dispatch }, data) {
			if (isWebview()) {
				if (!window.ReactNativeWebView?.postMessage) {
					reportPrintLog('轉交Native失敗', {
						machineType: data.machineType,
						messageId: data.messageId,
						reason: 'ReactNativeWebView不可用',
					})
					return false
				}
				const connected = getPrinterConnected(rootState, data.machineType)
				reportPrintLog('轉交Native列印', {
					machineType: data.machineType,
					messageId: data.messageId,
					connected,
					posPrinterStatus: rootState.order.posPrinterStatus,
					receipts: getReceiptMeta(data),
				})
				if (!connected) {
					reportPrintLog('印表機未連線', {
						machineType: data.machineType,
						messageId: data.messageId,
						reason: '轉交Native前列印狀態為未連線',
					})
				}
				try {
					window.ReactNativeWebView.postMessage(
						JSON.stringify({
							type: 'print',
							data: data,
							cashDrawer: data.cashDrawer || false, // 新增錢箱控制參數
						}),
					)
					return true
				} catch (error) {
					reportPrintLog('轉交Native失敗', {
						machineType: data.machineType,
						messageId: data.messageId,
						reason: error?.message ?? String(error),
					})
					return false
				}
			}

			if (!WEB_BLUETOOTH_PRINT_ENABLED) {
				reportPrintLog('POS_WEB略過列印', {
					machineType: data.machineType,
					messageId: data.messageId,
					reason: '瀏覽器不驅動出單機，改由 APP 出紙',
				})
				$devLog(
					`%c瀏覽器不驅動出單機，略過列印 (${data.machineType})`,
					'color:orange',
				)
				return
			}

			let printerLock
			let waitReceiptQueue
			let printer
			if (data.machineType === 'tag') {
				$devLog(`不支持標籤列印`)
				reportPrintLog('標籤列印略過', {
					machineType: data.machineType,
					reason: 'Web端不支援標籤列印',
				})
				return
			} else if (data.machineType === 'kiosk') {
				printerLock = 'Kiosk'
				waitReceiptQueue = state.waitKioskReceiptQueue
				printer = rootState.order.BTKioskPrinter
			} else if (data.machineType === 'pos') {
				printerLock = 'Pos'
				waitReceiptQueue = state.waitPosReceiptQueue
				printer = rootState.order.BTPosPrinter
			} else {
				printerLock = 'PosSecond'
				waitReceiptQueue = state.waitPosSecondReceiptQueue
				printer = rootState.order.BTPosPrinterSecond
			}

			// 瀏覽器未連 Web Bluetooth 時不嘗試列印，避免與 App 並存時產生假 E-印表機未連線
			if (!printer) {
				reportPrintLog('POS_WEB未連線出單機', {
					machineType: data.machineType,
					messageId: data.messageId,
				})
				$devLog(
					`%cWeb 端未連線出單機，略過列印 (${data.machineType})`,
					'color:orange',
				)
				return
			}

			// 已上鎖的話,新增資料到Queue並返回
			if (state[`isLock${printerLock}Printer`]) {
				waitReceiptQueue.push(data)
				reportPrintLog('列印進入佇列', {
					printerLock,
					queueLength: waitReceiptQueue.length,
					machineType: data.machineType,
					messageId: data.messageId,
				})
				$devLog(
					`%s已上鎖 當前待列印數量:%s`,
					printerLock,
					waitReceiptQueue.length,
				)
				return
			}

			try {
				reportPrintLog('開始列印', {
					printerLock,
					machineType: data.machineType,
					messageId: data.messageId,
					connected: !!printer,
					receipts: getReceiptMeta(data),
				})
				$devLog('%s開始列印 進行上鎖', printerLock, data)
				state[`isLock${printerLock}Printer`] = true
				if (hasPrintableBillData(data.storeReceipt)) {
					await dispatch('print', {
						receipt: data.storeReceipt,
						blueTooth: printer,
						isBase64: false,
						machineType: data.machineType,
						messageId: data.messageId,
					})
				}
				if (hasPrintableBillData(data.kitchenReceipt)) {
					await dispatch('print', {
						receipt: data.kitchenReceipt,
						blueTooth: printer,
						isBase64: false,
						machineType: data.machineType,
						messageId: data.messageId,
					})
				}
				if (hasPrintableBillData(data.clientReceipt)) {
					await dispatch('print', {
						receipt: data.clientReceipt,
						blueTooth: printer,
						isBase64: false,
						machineType: data.machineType,
						messageId: data.messageId,
					})
				}
				if (hasPrintableBillData(data.invoice)) {
					await dispatch('print', {
						receipt: data.invoice,
						blueTooth: printer,
						isBase64: true,
						machineType: data.machineType,
						messageId: data.messageId,
					})
				}

				// 模擬延時,阻塞當前線程
				// setTimeout(function () {
				// 	dispatch('printerUnLock', printerLock)
				// }, 3000)

				await dispatch('printerUnLock', printerLock)
			} catch (error) {
				state[`isLock${printerLock}Printer`] = false
				reportPrintLog('列印異常', {
					printerLock,
					machineType: data.machineType,
					messageId: data.messageId,
					error: error?.message ?? String(error),
				})
				$devLog('%s出現異常 釋放鎖', printerLock)
				$devLog(`%c出現異常`, 'color:red;background-color:#fff;', error)
				throw error
			}
		},
		async print(
			_,
			{ receipt, blueTooth, isBase64 = false, machineType, messageId },
		) {
			if (!hasPrintableBillData(receipt)) return

			const billLength = getBillLength(receipt)
			if (blueTooth != null) {
				reportPrintLog('單張BILL單開始列印', {
					machineType,
					messageId,
					isBase64,
					billLength,
				})
				// 根據格式使用不同的分片大小：Base64 使用較大的 128 bytes，一般格式使用 48 bytes
				const SPLICE_NUM = isBase64 ? 128 : 48
				// 統一使用 Buffer API 處理 base64 數據（webpack 已提供 buffer polyfill）
				let unit8Arr
				try {
					unit8Arr = Buffer.from(receipt, 'base64')
				} catch (error) {
					reportPrintLog('BILL單格式錯誤', {
						machineType,
						messageId,
						isBase64,
						billLength,
						error: error?.message ?? String(error),
					})
					return
				}
				if (unit8Arr.length === 0) {
					return
				}
				const len = Math.ceil(unit8Arr.length / SPLICE_NUM)
				let hasWriteError = false

				for (let i = 0; i < len; i++) {
					const value = unit8Arr.slice(i * SPLICE_NUM, (i + 1) * SPLICE_NUM)
					try {
						await blueTooth.writeValue(value)
					} catch (error) {
						hasWriteError = true
						reportPrintLog('藍牙寫入失敗', {
							machineType,
							messageId,
							isBase64,
							chunkIndex: i,
							totalChunks: len,
							error: error?.message ?? String(error),
						})
					}
				}
				reportPrintLog(
					hasWriteError ? '單張BILL單列印失敗' : '單張BILL單列印完成',
					{
						machineType,
						messageId,
						isBase64,
						billLength,
						totalChunks: len,
					},
				)
			} else {
				reportPrintLog('印表機未連線', {
					machineType,
					messageId,
					isBase64,
					billLength,
				})
				$devLog(`%c印表機沒有連線`, 'color:red;background-color:#fff;')
			}
		},
		printerUnLock({ state, dispatch }, name) {
			if (state[`wait${name}ReceiptQueue`].length > 0) {
				const nextData = state[`wait${name}ReceiptQueue`][0]
				reportPrintLog('佇列續印', {
					printerLock: name,
					queueLength: state[`wait${name}ReceiptQueue`].length,
					messageId: nextData.messageId,
				})
				$devLog(
					`${name} 待列印數量為:%s 繼續列印`,
					state[`wait${name}ReceiptQueue`].length,
					nextData,
				)
				state[`isLock${name}Printer`] = false
				state[`wait${name}ReceiptQueue`].shift()
				dispatch('printWithDataAndPlatform', nextData)
			} else {
				reportPrintLog('佇列清空釋放鎖', {
					printerLock: name,
				})
				$devLog(`${name} 待列印數量為0 釋放鎖`)
				state[`isLock${name}Printer`] = false
			}
		},
		/**
		 * Native 已實際確認目標藍牙不可用；只在這台 APP 保存三分鐘略過紀錄。
		 * 不送 ACK，讓同店其他已連線裝置仍有機會承接補送。
		 */
		suppressAutomaticPrint({ state }, data) {
			state.nativePrintsInFlight.delete(getPrintJobKey(data))
			const stored = persistPrintSuppression(data)
			reportPrintLog('列印本機略過已記錄', {
				machineType: data.machineType,
				messageId: data.messageId,
				stored,
			})
		},
		/**
		 * 停止自動補送的 ACK：Native 實際確認目標藍牙可用並接受列印後觸發。
		 * 同一張單的重複回報會由 deliveredMessages 去重。
		 * storeId、messageId、machineType 必須從 WebSocket 推送原樣帶入，不能使用
		 * 當下選擇的店家或自行推導機型；後端拿這些欄位做 CAS 條件。
		 */
		acknowledgeDelivered({ state }, { storeId, messageId, machineType }) {
			state.nativePrintsInFlight.delete(
				getPrintJobKey({ storeId, messageId, machineType }),
			)
			if (!storeId || !messageId) {
				reportPrintLog('列印已交付回報略過', {
					messageId,
					reason: !messageId ? '無 messageId' : '無 storeId',
				})
				return
			}
			if (state.deliveredMessages.has(messageId)) {
				return
			}

			// 先寫表再打 API：回報是非同步的，若等 API 回來才寫，這段空窗內來的補送
			// 會被當成新單再交付一次而重複出紙。
			state.deliveredMessages.set(messageId, Date.now())
			pruneMessageMap(state.deliveredMessages, DELIVERED_MESSAGE_TTL_MS)

			posApi
				.printDelivered({ storeId: Number(storeId), messageId, machineType })
				.catch(err => {
					// ACK 沒送到時後端仍是 PENDING。移除紀錄讓下一次推送重新走完整流程：
					// 若前一次已交給 Native，由 deliveredAt 擋重印；未連線的工作另由
					// localStorage 略過表阻擋，不會因稍後手動連線而自動出紙。
					state.deliveredMessages.delete(messageId)
					reportPrintLog('列印已交付回報失敗', {
						messageId,
						error: err?.message ?? String(err),
					})
				})
		},
		resubscribe({ state }, { key, destination, callback, skipDedupe = false }) {
			// 訂閱或重新訂閱 WebSocket 通道，並提供消息去重機制
			// skipDedupe：列印通道專用。後端補償重送沿用同一 messageId，
			// 套用 processedMessages 的通用去重會把全部補送擋掉、補送機制靜默失效。
			// 列印通道改用 deliveredMessages（Native 確認可列印後送出 ACK 時寫入）判定。

			// 先退掉同一個 key 的舊訂閱再建新的。直接覆寫的話舊訂閱還活著，
			// 同一則推播會進 callback 兩次；列印通道的 skipDedupe 又拿掉了
			// processedMessages 這層遮蔽，兩次都會走到出單流程而重複出紙。
			if (state.subscriptions[key]) {
				try {
					state.subscriptions[key].unsubscribe()
				} catch (err) {
					// 連線已斷時 unsubscribe 會拋，忽略即可——舊訂閱本來就跟著連線一起沒了
					$devLog(`%c退訂舊的 ${key} 失敗（可忽略）: ${err}`, 'color:orange')
				}
				state.subscriptions[key] = null
			}

			const client = state.stompClient
			const epoch = state.socketEpoch
			state.subscriptions[key] = client.subscribe(destination, msg => {
				if (epoch !== state.socketEpoch || client !== state.stompClient) {
					$devLog(`%c忽略舊連線訊息 (${destination})`, 'color:orange')
					return
				}
				if (!skipDedupe) {
					let messageId
					try {
						messageId = JSON.parse(msg.body)?.messageId
					} catch (err) {
						$devLog(`%c訂閱訊息解析失敗 (${destination}): ${err}`, 'color:red')
						return
					}

					// 消息去重：如果有 messageId 且已處理過，則忽略
					if (messageId && state.processedMessages.has(messageId)) {
						$devLog(
							`%c忽略重複消息: ${messageId} (${destination})`,
							'color:orange',
						)
						return
					}

					// 記錄已處理的消息（僅當有 messageId 時）
					if (messageId) {
						state.processedMessages.set(messageId, Date.now())
					}
				}

				// 執行回調處理消息
				callback(msg)

				// 定期清理過期消息記錄（保留 3 分鐘），超量時淘汰最舊的而非整表清空
				pruneMessageMap(state.processedMessages, 3 * 60 * 1000)
			})
			$devLog(`%c已訂閱 ${key} → ${destination}`, 'color:blue')
		},
	},
}
